import { useEffect, useRef, useState } from "react";
import requestChatStream from "./Conversation.lib.streamRequest";

// Live token stream for a single chat.
//
// The connection is NOT opened when the chat mounts: the backend signals
// STREAM_READY over the websocket only for nodes that actually stream (LLM/agent).
// A plain form never streams, so it never opens a connection — its reply arrives
// through the regular message refetch instead.

const THINKING = "THINKING";
const OUTPUT = "OUTPUT";

// Recognises the websocket payload that announces a stream is available.
const isStreamReadySignal = (data) => data?.status == "STREAM_READY";

const useChatStream = (chatId, { onDone } = {}) => {

  const abortRef = useRef();
  const isOpenRef = useRef(false);
  const phaseRef = useRef(null);
  // Kept in a ref so the stream callbacks always see the latest closure without
  // reopening the connection on every render.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const [isStreaming, setIsStreaming] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [thinkingText, setThinkingText] = useState("");
  const [streamingText, setStreamingText] = useState("");
  // What the backend says the agent is doing right now ("Buscando en internet").
  const [activityText, setActivityText] = useState("");

  const clearText = () => {
    setThinkingText("");
    setStreamingText("");
    setActivityText("");
  };

  const reset = () => {
    isOpenRef.current = false;
    phaseRef.current = null;
    setStartedAt(null);
    setIsStreaming(false);
    clearText();
  };

  const onStreamEvent = (event) => {

    // "waiting" only means the connection is up — no work signalled yet, stay quiet.
    if (event.type == "start") {
      phaseRef.current = THINKING;
      clearText();
      setIsStreaming(true);
    }
    else if (event.type == "reasoning") {
      phaseRef.current = THINKING;
      setThinkingText(prev => prev + event.delta);
      setIsStreaming(true);
    }
    else if (event.type == "delta") {
      // The first output delta opens the real answer. Reasoning text is kept so the
      // UI can show both (thinking + answer) separated by a divider.
      setStreamingText(prev => phaseRef.current == THINKING ? event.delta : prev + event.delta);
      phaseRef.current = OUTPUT;
      // The answer is being written: the tool that produced it is done.
      setActivityText("");
      setIsStreaming(true);
    }
    else if (event.type == "status" || event.type == "tool") {
      // Tools the backend has no line for send none, and the generic state stays.
      setActivityText(event.message || "");
      setIsStreaming(true);
    }
    else if (event.type == "done") {
      reset();
      onDoneRef.current?.();
    }
  };

  // Opens a fresh connection, dropping any stream already in flight. Used when the
  // user explicitly asks for new output (regenerate, repeat execution).
  const startStream = () => {

    abortRef.current?.abort();
    isOpenRef.current = true;
    phaseRef.current = null;
    setStartedAt(Date.now());
    setIsStreaming(true);
    clearText();

    const controller = new AbortController();
    abortRef.current = controller;
    requestChatStream(chatId, onStreamEvent, controller.signal)
      .catch(error => {
        if (error.name != "AbortError") reset();
      });
  };

  // Reaction to STREAM_READY: show the streaming state immediately, but only open a
  // connection if one isn't already running — the backend resends the signal on
  // every execution step.
  const markStreamReady = () => {
    setIsStreaming(true);
    if (!isOpenRef.current) startStream();
  };

  // A bot message stored after the stream opened is the very text the live bubble is
  // already painting; rendering both would duplicate it. It has to be painting it,
  // though: the backend resends STREAM_READY on every execution step, so a turn resumed
  // from a form or a UI tool opens a connection that never sends a token. Hiding the
  // stored answer on the promise of a stream left it invisible — and the thinking bubble
  // up — until the connection closed on its own, half a minute later.
  const isRenderedByStream = (message) =>
    isStreaming &&
    startedAt != null &&
    streamingText != "" &&
    message.type == "BOT_MESSAGE" &&
    new Date(message.createdAt).getTime() >= startedAt;

  // The stored answer arrived and the stream never wrote a word, so there is no live
  // bubble to wait for: drop the connection instead of animating a turn that is over.
  const endIfSilent = () => {
    if (!isStreaming || streamingText != "") return;
    abortRef.current?.abort();
    reset();
  };

  useEffect(() => () => abortRef.current?.abort(), [chatId]);

  return {
    activityText,
    isStreaming,
    thinkingText,
    streamingText,
    isRenderedByStream,
    endIfSilent,
    startStream,
    markStreamReady,
  };
};

export { isStreamReadySignal };
export default useChatStream;
